import { useAuth0 } from '@auth0/auth0-react';
import amplitudeLog from '@client/amplitude';
import {
  AMP_RECIPE_CREATE,
  AMP_RECIPE_DELETE,
  AMP_RECIPE_UPDATE_DESCRIPTION,
  AMP_RECIPE_UPDATE_NAME,
  AMP_RECIPE_MENU_ACTION_CLEAR,
  AMP_RECIPE_MENU_ACTION_DETACH,
  AMP_RECIPE_MENU_ACTION_RESET,
  AMP_RECIPE_MENU_ACTION_SAVE,
  AMP_RECIPE_MENU_ACTION_SAVE_AS,
} from '@shared/constants';
import { useIsConfirmed } from '@client/hooks/use-confirm.hook';
import { usePromiseSnackbar } from '@client/hooks/use-snackbar.hooks';
import { useIsReadonlyElement } from '@client/hooks/use-user.hooks';
import { useBooleanState } from '@client/hooks/use-utils.hooks';
import {
  useOrganizations,
  useSelectedOrganizationName,
} from '@client/store/organization';
import { useApplyBaselineRecipeOverwrite } from '@client/hooks/use-apply-baseline-recipe-overwrite.hook';
import {
  useCreateRecipe,
  useDeleteRecipe,
} from '@client/store/recipe/recipe.hook';
import { TEMPLATE_ORGANIZATION } from '@shared/constants';
import { isTemplateOrganizationItem } from '@shared/helpers/organization.helpers';
import {
  createRecipeFromElement,
  isDeletedRecipe,
} from '@shared/helpers/recipe.helpers';
import { IElement } from '@shared/models/project.model';
import { Recipe } from '@shared/models/recipe.model';
import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import * as uuid from 'uuid';
import { Menu } from '@ui/menus/Menu';
import { ElementPropertyMenuItems } from '@ui/menus/ElementPropertyMenu';

import CloneRecipesDialog from './dialogs/CloneRecipesDialog';
import EditRecipeDialog from './dialogs/EditRecipeDialog';
import UpdateRecipesDialog from './dialogs/UpdateRecipesDialog';
import { useRecipeContextElement } from '@client/hooks/use-recipe-context-element.hooks';
import { useSelectedProposalId } from '@client/hooks/use-selected-proposal-id.hooks';
import {
  useAllRecipeElementsInProject,
  useApplyRecipe,
  useRecipeIsModified,
  useResetRecipe,
  useUpdateRecipeFromElement,
} from '../use-recipes.hooks';
import { useElementResults } from '@features/results/use-results.hooks';
import { IconButton } from '@ui/buttons';
import { Icon } from '@ui/Icon';

type RecipeActionMenuProps = {
  element: IElement;
  recipe?: Recipe;
};

const RecipeActionMenu: FC<RecipeActionMenuProps> = ({ element, recipe }) => {
  const contextElement = useRecipeContextElement(element) ?? element;
  // Full element totals. The chart's lifecycle filter must not shrink what Save persists.
  const elementResults = useElementResults(contextElement, undefined, false);
  const { user } = useAuth0();

  const applyRecipe = useApplyRecipe();
  const createRecipe = useCreateRecipe();
  const deleteRecipe = useDeleteRecipe();
  const resetRecipe = useResetRecipe();
  const applyBaselineRecipeOverwrite = useApplyBaselineRecipeOverwrite();
  const selectedProposalId = useSelectedProposalId();
  const updateRecipeFromElement = useUpdateRecipeFromElement();
  const confirm = useIsConfirmed();
  const promiseSnackbar = usePromiseSnackbar();

  const isReadonly = useIsReadonlyElement(element);
  const recipeIsModified = useRecipeIsModified(contextElement);

  const organizations = useOrganizations();
  const elementsWithRecipe = useAllRecipeElementsInProject(recipe);
  const selectedOrganization = useSelectedOrganizationName();

  const [isOpenCloneRecipesDialogOpen, hideCloneRecipesDialog] =
    useBooleanState(false);

  const [updateDialogVisible, showUpdateDialog, hideUpdateDialog] =
    useBooleanState(false);

  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
  const [editingRecipe, setEditing] = useState<Recipe | undefined>(undefined);

  const isLibraryRecipe = isTemplateOrganizationItem(recipe);

  const modificationOfLibraryRecipeDisabled =
    selectedOrganization !== TEMPLATE_ORGANIZATION && isLibraryRecipe;

  // A soft-deleted recipe stays resolvable but cannot be modified or re-saved.
  const isDeleted = recipe ? isDeletedRecipe(recipe) : false;

  const elementHasNoChildren = contextElement.elements.length === 0;

  const saveRecipeTooltipTitle = useMemo(() => {
    if (elementHasNoChildren) {
      return 'Cannot save recipe with no children';
    }
    if (modificationOfLibraryRecipeDisabled) {
      return 'Cannot save library recipe';
    }
    if (isDeleted) {
      return 'Cannot save a deleted recipe';
    }
    return '';
  }, [elementHasNoChildren, modificationOfLibraryRecipeDisabled, isDeleted]);

  const onMenuClick = useCallback((e: React.MouseEvent<HTMLElement>) => {
    setMenuAnchor(e.currentTarget);
  }, []);

  const closeMenu = useCallback(() => {
    setMenuAnchor(null);
  }, []);

  const resetToRecipe = useCallback(() => {
    closeMenu();
    if (recipe) {
      resetRecipe(contextElement, recipe);
    }
    amplitudeLog(AMP_RECIPE_MENU_ACTION_RESET, {
      RecipeID: recipe?.id,
      ElementID: contextElement.id,
    });
  }, [contextElement, closeMenu, recipe, resetRecipe]);

  const handleRecipeDelete = useCallback(async () => {
    closeMenu();

    const confirmed = await confirm({
      title: 'Delete recipe?',
      description: 'This will delete the recipe. This action cannot be undone.',
      cancellationText: 'Cancel',
      confirmationText: 'Delete',
    });

    if (recipe && confirmed) {
      // Clear the element link before soft-delete when this element actually uses the
      // recipe. `none` removes recipe children so enrich cannot replay a detached `:r:`
      // subtree alongside baseline children (Duplicate ids in project).
      promiseSnackbar(
        (async () => {
          if (contextElement.recipe_id === recipe.id) {
            await applyRecipe(element, 'none', { silent: true });
          }
          await deleteRecipe(recipe.id);
          amplitudeLog(AMP_RECIPE_DELETE, {
            RecipeID: recipe?.id,
            ElementID: contextElement.id,
          });
        })(),
        {
          successMessage: `Recipe "${recipe.name}" deleted`,
          errorMessage: `Recipe "${recipe.name}" deletion failed`,
        },
      ).catch((err) => console.error(err.name, err));
    }
  }, [
    closeMenu,
    confirm,
    recipe,
    promiseSnackbar,
    deleteRecipe,
    contextElement,
    element,
    applyRecipe,
  ]);

  const beginRecipeEdit = useCallback(() => {
    setEditing(recipe);
    closeMenu();
    amplitudeLog('Recipe - Edit', {
      RecipeID: recipe?.id,
      ElementID: contextElement.id,
    });
  }, [contextElement.id, closeMenu, recipe]);

  const endRecipeEdit = useCallback(() => {
    setEditing(undefined);
  }, []);

  const handleEditRecipe = useCallback(
    async (updatedRecipe: Recipe) => {
      endRecipeEdit();

      // Create new recipe
      if (!updatedRecipe.id) {
        // Remap property ids and reference to recipe
        const id = uuid.v4();
        const properties = updatedRecipe.properties.map((property) => ({
          ...property,
          id: uuid.v4(),
          recipe_id: id,
        }));

        const createdRecipe = await createRecipe({
          ...updatedRecipe,
          id,
          properties,
        });
        if (selectedProposalId) {
          await applyRecipe(element, createdRecipe.id);
        } else {
          await applyBaselineRecipeOverwrite(
            [{ elementId: element.id, recipe: createdRecipe }],
            {
              snackbar: {
                type: 'undo',
                message: 'Recipe applied',
              },
            },
          );
        }
        amplitudeLog(AMP_RECIPE_CREATE, { RecipeId: id });
      }
      // Modify existing recipe
      else {
        updateRecipeFromElement(updatedRecipe, contextElement);
        if (recipe && recipe.name !== updatedRecipe.name) {
          amplitudeLog(AMP_RECIPE_UPDATE_NAME, { RecipeId: updatedRecipe.id });
        }
        if (recipe && recipe.description !== updatedRecipe.description) {
          amplitudeLog(AMP_RECIPE_UPDATE_DESCRIPTION, {
            RecipeId: updatedRecipe.id,
          });
        }
      }
    },
    [
      applyRecipe,
      applyBaselineRecipeOverwrite,
      createRecipe,
      contextElement,
      element,
      endRecipeEdit,
      recipe,
      selectedProposalId,
      updateRecipeFromElement,
    ],
  );

  const handleRecipeUpdate = useCallback(async () => {
    if (!recipe) {
      return;
    }

    amplitudeLog(AMP_RECIPE_MENU_ACTION_SAVE, {
      RecipeID: recipe?.id,
      ElementID: contextElement.id,
    });

    // Show dialog when the recipe is applied to other elements besides the selected one
    if (elementsWithRecipe.length > 1) {
      showUpdateDialog();
      return;
    }
    const updatedRecipe = await updateRecipeFromElement(
      recipe,
      contextElement,
      elementResults,
    );

    await applyBaselineRecipeOverwrite(
      [{ elementId: contextElement.id, recipe: updatedRecipe }],
      {
        snackbar: {
          type: 'undo',
          message: 'Recipe saved',
        },
      },
    );

    closeMenu();
  }, [
    recipe,
    elementsWithRecipe.length,
    elementResults,
    updateRecipeFromElement,
    contextElement,
    closeMenu,
    showUpdateDialog,
    applyBaselineRecipeOverwrite,
  ]);

  const handleDetach = useCallback(async () => {
    await applyRecipe(element, 'detach');
    closeMenu();
    amplitudeLog(AMP_RECIPE_MENU_ACTION_DETACH, {
      RecipeID: recipe?.id,
      ElementID: contextElement.id,
    });
  }, [applyRecipe, element, contextElement, closeMenu, recipe?.id]);

  const handleClear = useCallback(async () => {
    await applyRecipe(element, 'none');
    closeMenu();
    amplitudeLog(AMP_RECIPE_MENU_ACTION_CLEAR, {
      RecipeID: recipe?.id,
      ElementID: contextElement.id,
    });
  }, [applyRecipe, element, contextElement, closeMenu, recipe?.id]);

  const handleSaveRecipeAs = useCallback((): void => {
    // TODO Update at API
    setEditing(
      createRecipeFromElement(user?.sub || '', contextElement, elementResults),
    );
    closeMenu();
    amplitudeLog(AMP_RECIPE_MENU_ACTION_SAVE_AS, {
      RecipeID: recipe?.id,
      ElementID: contextElement.id,
    });
  }, [contextElement, elementResults, closeMenu, recipe?.id, user?.sub]);

  useEffect(() => {
    // Close menu if edit dialog opens
    if (editingRecipe) {
      closeMenu();
    }
  }, [editingRecipe, closeMenu]);

  if (!user) {
    return null;
  }

  const saveDisabledTooltip = elementHasNoChildren
    ? 'Cannot save recipe with no children'
    : undefined;
  return (
    <>
      <IconButton
        category="mui"
        icon="MoreVert"
        shape="square"
        aria-label="Recipe options"
        onClick={onMenuClick}
        disabled={isReadonly}
      />
      {user?.sub && (
        <Menu
          open={Boolean(menuAnchor)}
          anchorEl={menuAnchor}
          onClose={closeMenu}
          width={260}
        >
          {recipeIsModified.status === 'ready' &&
            recipeIsModified.isModified && (
              <>
                <Menu.Item
                  icon={<Icon category="mui" icon="SaveAlt" />}
                  onClick={() => {
                    elementHasNoChildren ? undefined : handleRecipeUpdate();
                  }}
                  disabled={
                    isReadonly ||
                    modificationOfLibraryRecipeDisabled ||
                    isDeleted
                      ? saveRecipeTooltipTitle || true
                      : false
                  }
                >
                  <FormattedMessage
                    id="element_recipe.save"
                    defaultMessage="Save"
                  />
                </Menu.Item>
                <Menu.Item
                  icon={<Icon category="mui" icon="Replay" />}
                  onClick={resetToRecipe}
                >
                  <FormattedMessage
                    id="element_recipe.reset"
                    defaultMessage="Reset"
                  />
                </Menu.Item>
              </>
            )}

          <Menu.Item
            icon={<Icon category="mui" icon="Save" />}
            disabled={
              isReadonly || elementHasNoChildren ? saveDisabledTooltip : false
            }
            onClick={handleSaveRecipeAs}
          >
            <FormattedMessage
              id="element_recipe.save_as"
              defaultMessage="Save as..."
            />
          </Menu.Item>

          <Menu.Submenu
            label="Add property"
            icon={<Icon category="mui" icon="AddCircleOutline" />}
            disabled={isReadonly}
            menuWidth={280}
          >
            <ElementPropertyMenuItems
              element={element}
              onItemSelect={closeMenu}
            />
          </Menu.Submenu>

          {recipe && (
            <>
              <Menu.Divider />
              <Menu.Item
                icon={<Icon category="mui" icon="Edit" />}
                onClick={beginRecipeEdit}
                disabled={
                  isReadonly || modificationOfLibraryRecipeDisabled || isDeleted
                }
              >
                <FormattedMessage
                  id="element_recipe.edit"
                  defaultMessage="Edit..."
                />
              </Menu.Item>
              <Menu.Item
                icon={<Icon category="mui" icon="Eject" />}
                onClick={handleDetach}
              >
                <FormattedMessage
                  id="element_recipe.detach"
                  defaultMessage="Detach"
                />
              </Menu.Item>
              <Menu.Item
                icon={<Icon category="mui" icon="Clear" />}
                onClick={handleClear}
              >
                <FormattedMessage
                  id="element_recipe.clear"
                  defaultMessage="Clear"
                />
              </Menu.Item>
              <Menu.Item
                icon={<Icon category="mui" icon="DeleteForever" />}
                onClick={handleRecipeDelete}
                disabled={
                  isReadonly || modificationOfLibraryRecipeDisabled || isDeleted
                }
              >
                <FormattedMessage
                  id="element_recipe.delete"
                  defaultMessage="Delete"
                />
              </Menu.Item>
            </>
          )}
        </Menu>
      )}
      {editingRecipe && (
        <EditRecipeDialog
          element={contextElement}
          editingRecipe={editingRecipe}
          selectedRecipe={recipe}
          elementCategoryId={contextElement.category_id}
          onClose={endRecipeEdit}
          onConfirm={handleEditRecipe}
        />
      )}
      {isOpenCloneRecipesDialogOpen && (
        <CloneRecipesDialog
          element={contextElement}
          open={true}
          organizations={organizations}
          onClose={hideCloneRecipesDialog}
        />
      )}
      {recipe && updateDialogVisible && (
        <UpdateRecipesDialog
          element={contextElement}
          recipe={recipe}
          visible={true}
          hideDialog={hideUpdateDialog}
        />
      )}
    </>
  );
};

export default RecipeActionMenu;
